import {Draggable, Droppable} from '@hello-pangea/dnd';
import {Box, Paper, useTheme} from '@mui/material';
import {ApplinearLoader} from 'components/loaders';
import {useDebounceEffect, useGetAllITemDataCursorPagination, useItemStatuesInRedux} from 'hooks';
import {useTenantSettingHelper} from 'hooks/settings';
import {defaultTo, sortBy} from 'lodash';
import {BoardView, ItemDataFilterRequest, ItemStatus} from 'models/board';
import {TenantSettingsKeysEnum} from 'models/settings';
import {useItemDataState} from 'providers';
import React, {useEffect, useMemo} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {appRoutes} from 'routes';
import {boardSelector} from 'store/boards/boardSlice';
import {itemStatusSelector, setReloadTheStatusItemsById} from 'store/itemStatues';
import {sprintSelector} from 'store/sprints';
import {getColumnDragWrapperStyle, getColumnDropWrapperStyle, getKanbanItemDragWrapper} from 'styles';
import {KanbanItemSkelotion} from '../KandabSkeletons/KanbanItemSkelotion';
import {AddKanbanItem, KanbanItem} from '../KandanItems';

interface KanbanColumnProps {
    column: ItemStatus;
    index: number;
    selectedBoardView: BoardView;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({column, index, selectedBoardView}) => {
    const theme = useTheme();
    const {
        getAllITemDataCursorPagination,
        getAllITemDataCursorPaginationLoading,
        getAllITemDataCursorPaginationResponse,
    } = useGetAllITemDataCursorPagination();

    const {clearBoardItemData, itemsDataState, addBoardItemsData, appendBoardItemsDataUnique} = useItemDataState();
    const {reloadTheStatusItemsById, statusItemsCount} = useSelector(itemStatusSelector);
    const dispatch = useDispatch();
    const {search} = useSelector(boardSelector);
    const {tenantId, workspaceId, boardId} = useParams();
    const navigate = useNavigate();
    const {setStatusItemsCountInRedux} = useItemStatuesInRedux();

    const {selectedSprint} = useSelector(sprintSelector);
    const {isSettingEnabled} = useTenantSettingHelper();

    useDebounceEffect(
        () => {
            const variables: ItemDataFilterRequest = {
                sprintIds: [selectedSprint?.id ?? ''],
                itemStatusIds: [column.id],
                filters: {
                    search: search,
                },
            };

            const isSprintEnabled = isSettingEnabled([TenantSettingsKeysEnum.TenantEnableSprints]);

            clearBoardItemData();
            if (isSprintEnabled) {
                if (selectedSprint != null)
                    getAllITemDataCursorPagination({
                        variables: {
                            filters: variables,
                            first: 50,
                        },
                    });
            } else {
                getAllITemDataCursorPagination({
                    variables: {
                        filters: variables,
                        first: 50,
                    },
                });
            }
        },
        [column.id, selectedSprint, search],
        300,
    );

    useEffect(() => {
        if (getAllITemDataCursorPaginationResponse?.nodes && !getAllITemDataCursorPaginationLoading) {
            setStatusItemsCountInRedux({
                ...statusItemsCount,
                [column.id]: getAllITemDataCursorPaginationResponse.totalCount ?? 0,
            });
        }
        if (getAllITemDataCursorPaginationResponse?.nodes.length && !getAllITemDataCursorPaginationLoading) {
            appendBoardItemsDataUnique(getAllITemDataCursorPaginationResponse.nodes);

            if (reloadTheStatusItemsById) {
                dispatch(setReloadTheStatusItemsById(undefined));
            }
        }
    }, [getAllITemDataCursorPaginationResponse, getAllITemDataCursorPaginationLoading]);

    const handleNext = () => {
        const variables: ItemDataFilterRequest = {
            itemStatusIds: [column.id],
            sprintIds: [selectedSprint?.id ?? ''],
            filters: {
                search: search,
            },
        };
        if (!getAllITemDataCursorPaginationResponse?.pageInfo?.hasNextPage) return;
        getAllITemDataCursorPagination({
            variables: {
                filters: variables,
                first: 50,
                after: getAllITemDataCursorPaginationResponse?.pageInfo.endCursor,
            },
        });
    };

    const itemDataResponse = useMemo(() => {
        if (!itemsDataState) return [];
        return sortBy(
            itemsDataState.filter(item => item.itemStatusId === column.id),
            ['order'],
        );
    }, [itemsDataState, column.id]);

    if (!column) return null;

    return (
        <Draggable draggableId={column.id} isDragDisabled index={index}>
            {(provided, snapshot) => (
                <Paper
                    elevation={2}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                        ...getColumnDragWrapperStyle(snapshot.isDragging, provided.draggableProps.style, theme, '5px'),
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Box position="sticky" top={0} zIndex={1}>
                        <AddKanbanItem
                            addBoardItemsData={addBoardItemsData}
                            column={column}
                            itemsTotalCount={defaultTo(statusItemsCount[column.id], 0)}
                        />
                        {getAllITemDataCursorPaginationLoading && <ApplinearLoader />}
                    </Box>
                    <InfiniteScroll
                        scrollableTarget={`scrollableDiv-${column.id}`}
                        dataLength={itemDataResponse.length}
                        next={handleNext}
                        hasMore={!!getAllITemDataCursorPaginationResponse?.pageInfo?.hasNextPage}
                        loader={<></>}
                    >
                        <Droppable droppableId={column.id} type="item">
                            {(providedDrop, snapshotDrop) => (
                                <div
                                    id={`scrollableDiv-${column.id}`}
                                    ref={providedDrop.innerRef}
                                    {...providedDrop.droppableProps}
                                    style={getColumnDropWrapperStyle(snapshotDrop.isDraggingOver, theme, '0px')}
                                >
                                    {itemDataResponse.map((item, i) => (
                                        <Draggable key={item.id} draggableId={item.id} index={i}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getKanbanItemDragWrapper(
                                                        provided.draggableProps.style,
                                                        theme,
                                                    )}
                                                >
                                                    <Paper
                                                        elevation={2}
                                                        style={{padding: 10, marginBottom: 10}}
                                                        sx={{
                                                            borderRadius: '0px',
                                                            borderLeft: column.color
                                                                ? `4px solid ${column.color}`
                                                                : '4px solid #1976d2',
                                                        }}
                                                    >
                                                        <KanbanItem
                                                            itemClickHandler={() => {
                                                                navigate(
                                                                    appRoutes.workspace.boadListingWithTicket(
                                                                        tenantId,
                                                                        workspaceId,
                                                                        boardId,
                                                                        item.ticketNo.toString(),
                                                                    ),
                                                                );
                                                            }}
                                                            selectedBoardView={selectedBoardView}
                                                            key={item.id}
                                                            item={item}
                                                        />
                                                    </Paper>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {getAllITemDataCursorPaginationLoading && itemDataResponse.length === 0 && (
                                        <KanbanItemSkelotion />
                                    )}
                                    {providedDrop.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </InfiniteScroll>
                </Paper>
            )}
        </Draggable>
    );
};

export default KanbanColumn;
